import type { KeyboardEvent, MouseEventHandler, ReactNode, Ref } from 'react'

import { XCircleIcon } from '@gto/icons-react/outline/x-circle'
import { cn } from '@gto/ui/cn'
import { Tooltip } from '@gto/ui/tooltip'

import { CHIP_MARKER, chipVariants } from './chip.styles'
import { ChipCheckIcon } from './chip-check-icon'

type ChipColor = 'default' | 'primary'
type GlassChipColor = ChipColor | 'warning' | 'info'
type ChipSize = 'large' | 'medium' | 'small' | 'xs'
type ChipVariant = 'filled' | 'outlined' | 'glass'

type CommonChipProps = {
  /** The chip label content */
  label: ReactNode
  /** Size variant. `large` is mobile-only — see the size notes in `chip.styles.ts`. */
  size?: ChipSize
  /** Glass only: tints from the caller's --accent-rgb, --accent-solid, --accent-bg-a and --accent-border-a, with green fallbacks. */
  accent?: 'custom'
  /** Optional start icon */
  icon?: ReactNode
  /** Whether the chip is disabled */
  disabled?: boolean
  /** Additional CSS class names */
  className?: string
  /** Sub-chips or other child content rendered after the label. Use with default color only — sub-chips are not intended for non-default chips. */
  children?: ReactNode
  /** Test attribute for E2E testing */
  'data-tst'?: string
}

type DefaultColorChipProps = CommonChipProps & {
  /** Color variant */
  color?: 'default'
  /** Callback when delete button is clicked — renders a × icon button at the end. Only supported with color="default". */
  onDelete?: () => void
  /** Tooltip shown on hover of the delete button. Only meaningful when onDelete is set. */
  deleteTooltip?: ReactNode
  /** Accessible label for the delete button. Defaults to "Remove" — pass a localized string from the consumer. */
  deleteAriaLabel?: string
}

type PrimaryColorChipProps = CommonChipProps & {
  /** Color variant — primary chips do not support delete */
  color: 'primary'
  /** onDelete is not supported with color="primary" */
  onDelete?: never
  /** deleteTooltip is not supported with color="primary" */
  deleteTooltip?: never
  /** deleteAriaLabel is not supported with color="primary" */
  deleteAriaLabel?: never
}

type BaseChipProps = (DefaultColorChipProps | PrimaryColorChipProps) & {
  /** Interactive chip styles */
  variant?: Exclude<ChipVariant, 'glass'>
  /**
   * Multi-select layout — reserves space for a trailing checkmark so toggling
   * `color` between `default` (unselected) and `primary` (selected) does not
   * shift the chip's total width. Valid with `size="medium"` and `size="large"`,
   * which run one mechanism rescaled per size — see the compounds in
   * `chip.styles.ts`. Any other size has no room for a check and the guard throws.
   * Incompatible with `icon` / `onDelete` / `children`.
   */
  multiSelect?: boolean
  /**
   * Marks the chip as the exclusive "reset" option inside a multi-select group
   * (the `Any` chip). The multi-select layout is preserved so the chip keeps the
   * same width as its siblings, but the checkmark is never drawn — even when
   * the chip is in `primary` color. Has no effect when `multiSelect` is false.
   */
  exclusive?: boolean
}

type ClickableChipProps = BaseChipProps & {
  /** Click handler — renders the chip as a <div role="button"> for accessibility (avoids nested button issue when onDelete is also provided) */
  onClick: MouseEventHandler<HTMLDivElement>
  /** React 19 ref prop */
  ref?: Ref<HTMLDivElement>
  /** Toggle state for a chip used as an on/off control — renders `aria-pressed`. */
  selected?: boolean
  /**
   * Disclosure state for a chip that reveals adjacent content — renders
   * `aria-expanded`.
   */
  expanded?: boolean
  /**
   * Accessible name for the chip's button root, overriding name-from-content.
   * Pass it when the label does not read as a name on its own — a glyph label
   * (card tiles carry suit by color) or an icon-only chip.
   */
  'aria-label'?: string
  /** Disclosure kind for a chip that opens a popup (a menu-anchor or dialog-opener chip). */
  'aria-haspopup'?: 'dialog' | 'menu'
}

type StaticChipProps = BaseChipProps & {
  onClick?: never
  /** React 19 ref prop */
  ref?: Ref<HTMLDivElement>
  /** Button-role ARIA — a static chip is a plain div, which cannot carry it. */
  selected?: never
  /** Button-role ARIA — a static chip is a plain div, which cannot carry it. */
  expanded?: never
  /** ARIA prohibits naming a `role=generic` element; only the button root can be named. */
  'aria-label'?: never
  /** Button-role ARIA — a static chip is a plain div, which cannot carry it. */
  'aria-haspopup'?: never
}

type GlassChipProps = CommonChipProps & {
  /** Glass is a static label with no interactive states. */
  variant: 'glass'
  color?: GlassChipColor
  onClick?: never
  onDelete?: never
  deleteTooltip?: never
  deleteAriaLabel?: never
  multiSelect?: never
  exclusive?: never
  selected?: never
  expanded?: never
  'aria-label'?: never
  'aria-haspopup'?: never
  ref?: Ref<HTMLDivElement>
}

type ChipProps = ClickableChipProps | StaticChipProps | GlassChipProps

const Chip = (props: ChipProps) => {
  const {
    label,
    color = 'default',
    size = 'medium',
    variant = 'filled',
    accent,
    icon,
    onDelete,
    deleteTooltip,
    deleteAriaLabel = 'Remove',
    disabled = false,
    className,
    children,
    multiSelect = false,
    exclusive = false,
    'data-tst': dataTst,
    'aria-label': ariaLabel,
    'aria-haspopup': ariaHasPopup,
    selected,
    expanded,
    ref,
  } = props

  if (onDelete && color !== 'default') {
    throw new Error(
      'Chip: onDelete is not supported with color="primary". Use color="default" instead.',
    )
  }

  // Allowlist, not denylist: only these two sizes have multi-select compounds in
  // `chipVariants`. A future size would otherwise silently fall through to medium's
  // layout inside its own shell instead of tripping this guard.
  if (multiSelect && size !== 'medium' && size !== 'large') {
    throw new Error(
      `Chip: multiSelect supports size="medium" and size="large" only (got "${size}").`,
    )
  }

  if (multiSelect && (icon != null || onDelete != null || children != null)) {
    throw new Error('Chip: multiSelect is incompatible with icon, onDelete, and children.')
  }

  const styles = chipVariants({
    color,
    size,
    variant,
    accent,
    disabled,
    clickable: Boolean(props.onClick),
    deletable: Boolean(onDelete),
    multiSelect,
    exclusive,
  })

  const innerContent = (
    <>
      {Boolean(icon) && <span className={styles.iconWrapper()}>{icon}</span>}
      <span className={styles.label()}>{label}</span>
      {children}
      {Boolean(onDelete) && (
        <div className={styles.deleteWrapper()}>
          <Tooltip content={deleteTooltip}>
            <button
              aria-label={deleteAriaLabel}
              className={styles.deleteBtn()}
              disabled={disabled}
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onDelete?.()
              }}
            >
              <XCircleIcon
                aria-hidden
                size="lg"
                viewBox="-1 -1 26 26"
              />
            </button>
          </Tooltip>
        </div>
      )}
      {multiSelect && (
        <span
          aria-hidden
          className={styles.checkIcon()}
          data-tst="chip-check"
        >
          <ChipCheckIcon active={!exclusive && color === 'primary'} />
        </span>
      )}
    </>
  )

  if (props.onClick) {
    return (
      <div
        ref={ref}
        aria-disabled={disabled}
        aria-expanded={expanded}
        aria-haspopup={ariaHasPopup}
        aria-label={ariaLabel}
        aria-pressed={selected}
        className={cn(styles.root(), CHIP_MARKER, className)}
        data-tst={dataTst}
        data-ui="chip"
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : props.onClick}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if (disabled) {
            return
          }
          // Only the chip's own root activates. A keydown on a NESTED control —
          // the delete button — bubbles here too, and handling it would
          // `preventDefault()` that button's native Enter activation and click
          // the ROOT instead: pressing Enter on ✕ ran the chip's onClick, so the
          // delete was reachable by pointer only.
          if (e.target !== e.currentTarget) {
            return
          }

          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.currentTarget.click()
          }
        }}
      >
        {innerContent}
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={cn(styles.root(), CHIP_MARKER, className)}
      data-tst={dataTst}
      data-ui="chip"
    >
      {innerContent}
    </div>
  )
}

export { Chip }
export type { ChipColor, ChipProps, ChipSize, ChipVariant }
